import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Field } from "@/pages/Companies";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { User, Phone, CheckCircle2, Briefcase, CalendarDays } from "lucide-react";
import ActivityAttachments from "@/components/ActivityAttachments";
import DateTimeSlotPicker from "@/components/DateTimeSlotPicker";

export default function ActivityDetailDialog({ activity, open, onClose, onSaved }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (open && activity) setDraft({ ...activity, deal_id: activity.deal_id || "none" });
  }, [open, activity]);

  const { data: deals = [] } = useQuery({
    queryKey: ["deals-all"], enabled: open,
    queryFn: async () => (await api.get("/deals")).data,
  });
  const { data: staff = [] } = useQuery({
    queryKey: ["staff-users"], enabled: open,
    queryFn: async () => (await api.get("/staff")).data,
  });
  const deal = deals.find((d) => d.id === draft?.deal_id);
  const { data: dealClient } = useQuery({
    queryKey: ["deal-client", deal?.company_id], enabled: open && !!deal?.company_id,
    queryFn: async () => {
      const [co, cts] = await Promise.all([
        api.get(`/companies`).then((r) => r.data.find((c) => c.id === deal.company_id)),
        api.get(`/contacts`, { params: { company_id: deal.company_id } }).then((r) => r.data),
      ]);
      const primary = cts.find((c) => c.is_primary) || cts[0];
      return { company: co, contact: primary };
    },
  });

  const save = useMutation({
    mutationFn: async (extra = {}) => {
      const d = deals.find((x) => x.id === draft.deal_id);
      const payload = {
        type: draft.type, title: draft.title, description: draft.description || null,
        due_at: draft.due_at || null, priority: draft.priority,
        deal_id: draft.deal_id === "none" ? null : draft.deal_id,
        company_id: d?.company_id || draft.company_id || null,
        contact_id: d?.contact_id || draft.contact_id || null,
        quote_id: draft.quote_id || null,
        assigned_to: draft.assigned_to || null,
        ...extra,
      };
      if (draft.id) return (await api.patch(`/activities/${draft.id}`, payload)).data;
      return (await api.post("/activities", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["activities"] });
      qc.invalidateQueries({ queryKey: ["daily-tasks"] });
      qc.invalidateQueries({ queryKey: ["deal-activities"] });
      // Iter-67: also invalidate the Scheduler + bookings feeds so a
      // booking-linked task edit re-renders the Scheduler grid card.
      qc.invalidateQueries({ queryKey: ["scheduler-events"] });
      qc.invalidateQueries({ queryKey: ["bookings"] });
      toast.success("Saved");
      onSaved?.();
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  if (!draft) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-xl" data-testid="activity-detail-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{draft.id ? "Activity details" : "New activity"}</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
                <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="task">Task</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={draft.priority} onValueChange={(v) => setDraft({ ...draft, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Title" required><Input data-testid="activity-title-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></Field>
          <div>
            <Field label="Assigned to" required>
              <Select value={draft.assigned_to || "unassigned"} onValueChange={(v) => setDraft({ ...draft, assigned_to: v === "unassigned" ? null : v })}>
                <SelectTrigger data-testid="activity-assignee-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">— Unassigned —</SelectItem>
                  {staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          {/* Iter-73: shared calendar+slot picker so every New Activity flow has
              the same date + 30-min slot grid + "already booked that day"
              conflict warning that the Book Quote Appointment dialog uses. */}
          <div>
            <Field label="When">
              <DateTimeSlotPicker
                value={draft.due_at || ""}
                onChange={(v) => setDraft({ ...draft, due_at: v })}
                excludeActivityId={draft.id || null}
                testidPrefix="activity-when"
              />
            </Field>
          </div>
          {draft.mgmt_todo_id ? (
            <Field label={draft.risk_id ? "Linked register entry" : draft.issue_id ? "Linked issue" : "Linked meeting"}>
              <div className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-sm px-3 h-10">
                <button type="button"
                  onClick={() => { onClose(); nav(draft.risk_id ? "/management/risks" : draft.issue_id ? "/management/issues" : "/management/meetings"); }}
                  className="flex items-center gap-1.5 text-sm font-semibold text-cyan-700 hover:underline truncate"
                  data-testid="activity-meeting-link">
                  <CalendarDays size={13} className="shrink-0" />
                  {draft.risk_id
                    ? `View ${(draft.title || "").split(" — ")[0]} in the Risk & Opportunity register`
                    : draft.issue_id
                      ? `View ${(draft.title || "").split(" — ")[0]} in Issues (IDS)`
                      : "Meeting OS to-do — view minutes"}
                </button>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider shrink-0">synced</span>
              </div>
            </Field>
          ) : (
          <Field label="Linked job (deal)">
            {deal ? (
              <div className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-sm px-3 h-10">
                <button type="button" onClick={() => { onClose(); nav(`/deals?open=${deal.id}`); }}
                  className="flex items-center gap-1.5 text-sm font-semibold text-cyan-700 hover:underline truncate"
                  data-testid="activity-job-link">
                  <Briefcase size={13} className="shrink-0" />
                  {deal.job_number ? `${deal.job_number} — ` : ""}{deal.title}
                </button>
                <button type="button" onClick={() => setDraft({ ...draft, deal_id: "none" })}
                  className="text-[10px] text-slate-400 hover:text-rose-600 uppercase tracking-wider shrink-0"
                  data-testid="activity-job-unlink">change</button>
              </div>
            ) : (
              <Select value={draft.deal_id} onValueChange={(v) => setDraft({ ...draft, deal_id: v })}>
                <SelectTrigger data-testid="activity-job-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No job —</SelectItem>
                  {deals.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.job_number ? `${d.job_number} — ` : ""}{d.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          )}
          {deal && dealClient?.company && (
            <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2.5 space-y-1" data-testid="activity-client-card">
              <button type="button" onClick={() => { onClose(); nav(`/clients/${deal.company_id}`); }}
                className="flex items-center gap-1.5 text-sm font-semibold text-cyan-700 hover:underline" data-testid="activity-client-name-link">
                <User size={13} /> {dealClient.company.name}
              </button>
              {dealClient.contact && (
                <div className="flex items-center gap-1.5 text-xs text-slate-600">
                  <Phone size={11} />
                  {`${dealClient.contact.first_name || ""} ${dealClient.contact.last_name || ""}`.trim() || "Contact"}
                  {(dealClient.contact.phone || dealClient.contact.mobile_phone) && (
                    <span className="font-mono">· {dealClient.contact.phone || dealClient.contact.mobile_phone}</span>
                  )}
                </div>
              )}
            </div>
          )}
          <Field label="Description"><Textarea rows={3} value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></Field>
          {draft.id ? <ActivityAttachments activityId={draft.id} /> : (
            <p className="text-[11px] text-slate-400">Save first to attach files.</p>
          )}
        </div>
        <DialogFooter className="gap-2">
          {draft.id && !draft.completed && (
            <Button variant="outline" onClick={() => save.mutate({ completed: true })}
              className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50 mr-auto" data-testid="activity-complete-btn">
              <CheckCircle2 size={14} className="mr-1.5" /> Mark complete
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button data-testid="save-activity-btn" onClick={() => {
            if (!draft.title?.trim()) return toast.error("Title required");
            if (!draft.assigned_to) return toast.error("Activity must be assigned to a person");
            save.mutate({});
          }} className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
